// assistant-tool-helpers.ts — Shared pure value helpers for the AI tool layer.
// Imported by assistant-tools.ts (executeReadTool) and assistant-proposal-builder.ts (buildProposal).
// No imports from other assistant-* files to avoid circular dependencies.

/** Extract a trimmed string from an unknown value. */
export function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v.trim() : fallback;
}

/** Extract a finite number from an unknown value. */
export function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return isFinite(n) ? n : fallback;
}

/** Extract a boolean from an unknown value. */
export function bool(v: unknown, fallback = false): boolean {
  return typeof v === "boolean" ? v : fallback;
}

/** Extract an array of strings from an unknown value (filters non-strings). */
export function strArr(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}

/** Clamp n to [lo, hi]. */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** ISO date guard — returns the value if YYYY-MM-DD format, else fallback. */
export function safeDate(v: unknown, fallback: string): string {
  if (typeof v !== "string") return fallback;
  const trimmed = v.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : fallback;
}
